/**
 * GoalGauge — semicircle instrument for a goal-vs-actual ratio.
 *
 * Hand-rolled SVG on the same 180deg-to-0deg arc technique as DevicePieChart, so
 * the admin board needs no extra chart dependency.
 *
 * The goal always sits at 80% of the arc rather than at the end, so hitting the
 * target looks like a needle approaching the top of its range with headroom left,
 * not a bar that has run out of room. Tint carries the tier: teal on track, amber
 * behind, destructive below half the goal. Orange is deliberately absent — it is
 * reserved for brand and CTAs per DESIGN.md.
 */
import { goalTier, type GoalTier } from '@/lib/constants/adminGoals'
import { cn } from '@/lib/utils'

type GoalGaugeProps = {
  /** Achieved over goal, where 1 means the goal is met. Null renders an empty track. */
  ratio: number | null
  /** Center readout, e.g. "6.3%" or "12". */
  display: string
  /** Small caption under the readout, e.g. "of 120 target". */
  caption?: string
  className?: string
}

/** Fraction of the arc the goal marker sits at. */
const GOAL_ANCHOR = 0.8

const RADIUS = 56
const STROKE = 11
const CENTER_X = 70
const CENTER_Y = 66

const TIER_ARC: Record<GoalTier, string> = {
  onTrack: 'stroke-success dark:stroke-success',
  behind: 'stroke-warning dark:stroke-warning',
  critical: 'stroke-destructive',
}

export const TIER_DOT: Record<GoalTier, string> = {
  onTrack: 'bg-success',
  behind: 'bg-warning',
  critical: 'bg-destructive',
}

export const TIER_TEXT: Record<GoalTier, string> = {
  onTrack: 'text-success dark:text-success',
  behind: 'text-warning dark:text-warning',
  critical: 'text-destructive',
}

function polar(angle: number, radius: number) {
  return {
    x: CENTER_X + radius * Math.cos(angle),
    y: CENTER_Y - radius * Math.sin(angle),
  }
}

/** Arc path across `fraction` (0-1) of the semicircle, sweeping left to right. */
function arcPath(fraction: number): string {
  const clamped = Math.min(Math.max(fraction, 0), 1)
  const start = polar(Math.PI, RADIUS)
  const end = polar(Math.PI - clamped * Math.PI, RADIUS)
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x} ${end.y}`
}

export function GoalGauge({ ratio, display, caption, className }: GoalGaugeProps) {
  const tier = goalTier(ratio)
  const fill = tier != null ? Math.min((ratio as number) * GOAL_ANCHOR, 1) : 0

  const goalTick = {
    inner: polar(Math.PI - GOAL_ANCHOR * Math.PI, RADIUS - STROKE / 2 - 2),
    outer: polar(Math.PI - GOAL_ANCHOR * Math.PI, RADIUS + STROKE / 2 + 3),
  }

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <svg viewBox="0 0 140 78" className="w-full max-w-[168px]" role="img">
        <path
          d={arcPath(1)}
          fill="none"
          stroke="var(--muted)"
          strokeWidth={STROKE}
          strokeLinecap="round"
        />
        {tier != null && fill > 0 && (
          <path
            d={arcPath(fill)}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            className={cn('transition-all duration-500', TIER_ARC[tier])}
          />
        )}
        <line
          x1={goalTick.inner.x}
          y1={goalTick.inner.y}
          x2={goalTick.outer.x}
          y2={goalTick.outer.y}
          stroke="var(--foreground)"
          strokeWidth={1.5}
          strokeLinecap="round"
          opacity={0.5}
        />
        <text
          x={CENTER_X}
          y={CENTER_Y - 8}
          textAnchor="middle"
          className="fill-foreground text-[22px] font-semibold tabular-nums"
        >
          {display}
        </text>
        {caption && (
          <text
            x={CENTER_X}
            y={CENTER_Y + 8}
            textAnchor="middle"
            className="fill-muted-foreground text-[9px]"
          >
            {caption}
          </text>
        )}
      </svg>
    </div>
  )
}
